/** Width/height ratios at or below this are treated as vertical (9:16-ish). */
export const VERTICAL_ASPECT_MAX = 0.75;

/** Width/height ratios at or above this show crop controls (16:9-ish). */
export const WIDE_ASPECT_MIN = 1.2;

export const REEL_CROP_MODES = {
  LETTERBOXED: 'letterboxed',
  ZOOMED: 'zoomed',
};

export const DEFAULT_CROP_SETTINGS = {
  cropMode: REEL_CROP_MODES.ZOOMED,
  focalX: null,
  focalY: null,
};

export function computeAspectRatio(width, height) {
  if (!width || !height) return null;
  return Math.round((width / height) * 10000) / 10000;
}

export function isVerticalSource(aspectRatio) {
  return aspectRatio != null && aspectRatio <= VERTICAL_ASPECT_MAX;
}

export function isWideSource(aspectRatio) {
  return aspectRatio == null || aspectRatio >= WIDE_ASPECT_MIN;
}

/**
 * Crop window width as a fraction of source width when filling a 9:16 frame (object-cover).
 */
export function zoomedCropWidthFraction(aspectRatio) {
  if (!aspectRatio || aspectRatio <= 0) return 1;
  return Math.min(1, (9 / 16) / aspectRatio);
}

export function clampFocal(value) {
  return Math.min(1, Math.max(0, value));
}

export function focalToObjectPosition(focalX, focalY) {
  const x = focalX != null ? focalX : 0.5;
  const y = focalY != null ? focalY : 0.5;
  return `${x * 100}% ${y * 100}%`;
}

/**
 * Resolve CSS display props for a reel in the vertical feed.
 * NULL crop settings on wide sources preserve legacy center-cover behavior.
 */
export function resolveReelDisplay({
  sourceAspectRatio = null,
  cropMode = null,
  focalX = null,
  focalY = null,
} = {}) {
  if (isVerticalSource(sourceAspectRatio)) {
    return { objectFit: 'cover', objectPosition: '50% 50%' };
  }

  const mode = cropMode || REEL_CROP_MODES.ZOOMED;

  if (mode === REEL_CROP_MODES.LETTERBOXED) {
    return { objectFit: 'contain', objectPosition: '50% 50%' };
  }

  return {
    objectFit: 'cover',
    objectPosition: focalToObjectPosition(focalX, focalY),
  };
}

export function normalizeCropSettings({ cropMode, focalX, focalY } = {}) {
  const mode = cropMode === REEL_CROP_MODES.LETTERBOXED
    ? REEL_CROP_MODES.LETTERBOXED
    : REEL_CROP_MODES.ZOOMED;

  if (mode === REEL_CROP_MODES.LETTERBOXED) {
    return { cropMode: mode, focalX: null, focalY: null };
  }

  return {
    cropMode: mode,
    focalX: focalX != null ? clampFocal(focalX) : null,
    focalY: focalY != null ? clampFocal(focalY) : null,
  };
}

export function cropSettingsToDb({ sourceAspectRatio, cropMode, focalX, focalY }) {
  if (isVerticalSource(sourceAspectRatio)) {
    return {
      source_aspect_ratio: sourceAspectRatio,
      crop_mode: null,
      focal_x: null,
      focal_y: null,
    };
  }

  const normalized = normalizeCropSettings({ cropMode, focalX, focalY });
  return {
    source_aspect_ratio: sourceAspectRatio ?? null,
    crop_mode: normalized.cropMode,
    focal_x: normalized.focalX,
    focal_y: normalized.focalY,
  };
}
